import { useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import SearchInput from 'components/elements/SearchInput';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import {
  ALL_PROJECTS_TABLE_VIEW_TYPE,
  COMPANY_TABLE_VIEW_TYPE,
  type ListViewMode,
} from 'components/widgets/Dashboard/CompanyGroups/companyGroupTableViewMode';
import CompanyGroupViewModeToggle from 'components/widgets/Dashboard/CompanyGroups/CompanyGroupViewModeToggle';
import DashboardLeadsTable from 'components/widgets/Dashboard/DashboardLeadsTable';
import GroupSettingsMenu from 'components/widgets/Dashboard/GroupSettingsMenu';
import { useDebounce } from 'hooks/useDebounced';
import { useFetchCompanyLeads } from 'hooks/useFetchCompanyLeads';
import { useFilterGroupConfigLeads } from 'hooks/useFilterGroupConfigLeads';
import { useHiddenProjects } from 'hooks/useHiddenProjects';
import { usePaginate } from 'hooks/usePaginate';
import { useSortedProjects } from 'hooks/useSortedProjects';
import CompanyTable from 'pages/Dashboard/CompanyOverview/CompanyTable';
import { useDashboardGroupId, useDashboardGroupName } from 'state/dashboard';
import { CompanyLead, GroupRawResponse } from 'types';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { STATE_CODE_TO_LABEL } from 'utils/constants/location';

const containerRow = {
  alignItems: 'flex-start',
  height: '100%',
  width: '100%',
  '.chakra-link': {
    textDecoration: 'none',
  },
};

const containerColumn = {
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  gap: 'padding_16',
};

const filterMenuStyle = {
  gap: 'padding_16',
  justifyContent: 'space-between',
};

const filterRightClusterStyle = {
  gap: 'padding_16',
  alignItems: 'center',
};

const keywordInputStyles = {
  maxWidth: '240px',
  flex: 1,
};

type CompanySortKey = 'name' | 'activeProjects';

type CompanySortDirection = 'asc' | 'desc';

type Props = {
  companyMenu: boolean;
  setCompanyMenu: (value: boolean) => void;
  setCurrentCompany: (value: number) => void;
  setCurrentCompanyUuid: (value: string) => void;
  setCurrentCompanyName: (value: string) => void;
  groupData: GroupRawResponse | null;
};

const CompanyView = ({
  companyMenu,
  setCompanyMenu,
  setCurrentCompany,
  setCurrentCompanyUuid,
  setCurrentCompanyName,
  groupData,
}: Props) => {
  const [group] = useDashboardGroupId();
  const [groupName] = useDashboardGroupName();
  const [sortKey, setSortKey] = useState<CompanySortKey>('name');
  const [sortDirection, setSortDirection] = useState<CompanySortDirection>('asc');
  const [listView, setListView] = useState<ListViewMode>(COMPANY_TABLE_VIEW_TYPE);
  const [keywordFilter, setKeywordFilter] = useState('');
  const debouncedKeywordFilter = useDebounce(keywordFilter, 500);

  useEffect(() => {
    setListView(COMPANY_TABLE_VIEW_TYPE);
    setKeywordFilter('');
  }, [group]);

  const {
    nextPage,
    previousPage,
    page: currentPage,
    setPageNumber,
    resetPagination,
  } = usePaginate();
  const { sortArray } = useSortedProjects();

  const { data, isLoading: companyLeadsLoading } = useFetchCompanyLeads(Number(group));

  const { getFilteredLeads } = useFilterGroupConfigLeads({
    groupConfigId: Number(group),
    currentPage,
    sortArray,
    activeTabs: [],
    keywords: debouncedKeywordFilter,
    enabled: listView === ALL_PROJECTS_TABLE_VIEW_TYPE,
  });
  const { data: projectData, isLoading: allProjectsLoading } = getFilteredLeads;

  const { getAllHiddenProjectIdsForUser } = useHiddenProjects();
  const { data: hiddenProjectsSet } = getAllHiddenProjectIdsForUser;

  const { filteredProjectData, hiddenProjectsCount } = useMemo(() => {
    let countHiddenProjects = 0;
    if (!projectData?.results) {
      return {
        filteredProjectData: [],
        hiddenProjectsCount: 0,
      };
    }

    const filteredList = projectData.results.filter((item) => {
      if (hiddenProjectsSet?.has(item.slug)) {
        countHiddenProjects += 1;

        return false;
      }

      return true;
    });

    return {
      filteredProjectData: filteredList,
      hiddenProjectsCount: countHiddenProjects,
    };
  }, [projectData?.results, hiddenProjectsSet]);

  const totalProjectCount = useMemo(() => {
    if (!projectData?.count) {
      return 0;
    }

    return projectData.count - hiddenProjectsCount;
  }, [projectData?.count, hiddenProjectsCount]);

  const totalProjectPages = Math.ceil(totalProjectCount / 20) || 1;

  const sortedCompanies = useMemo(() => {
    if (!data) {
      return [];
    }

    const companies = [...data] as CompanyLead[];

    companies.sort((a, b) => {
      let comparison = 0;

      if (sortKey === 'name') {
        const nameA = a.name ?? '';
        const nameB = b.name ?? '';
        comparison = nameA.localeCompare(nameB);
      } else {
        const activeA = a.num_active_projects ?? 0;
        const activeB = b.num_active_projects ?? 0;
        comparison = activeA - activeB;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return companies;
  }, [data, sortKey, sortDirection]);

  const companyCount = sortedCompanies.length;
  const displayHeading = `${companyCount} companies in this group`;
  const groupCityName = useMemo(() => {
    // Newly created Company Group Configurations will not have a city trigger
    // Just generate the name via the metro code
    const groupState = groupData?.state_code;
    const regionLabel = STATE_CODE_TO_LABEL[groupState!];

    return regionLabel;
  }, [groupData]);

  const onListViewChange = (index: number) => {
    setListView(index === 1 ? ALL_PROJECTS_TABLE_VIEW_TYPE : COMPANY_TABLE_VIEW_TYPE);
    resetPagination();
  };

  const countHeadingText =
    listView === COMPANY_TABLE_VIEW_TYPE
      ? displayHeading
      : `${totalProjectCount} active projects found`;

  return (
    <FlexRow sx={containerRow}>
      {companyLeadsLoading ? (
        <Spinner />
      ) : (
        <FlexColumn sx={containerColumn}>
          <FlexRow>
            <FlexRow.Left>
              <Box>
                <Text.H3>{groupName}</Text.H3>
              </Box>
            </FlexRow.Left>
            <FlexRow.Right>
              <GroupSettingsMenu
                id={group ?? 'not_found'}
                group={groupData}
                showViewInMapButton={
                  listView === COMPANY_TABLE_VIEW_TYPE ? companyCount > 0 : true
                }
              />
            </FlexRow.Right>
          </FlexRow>
          <FlexRow sx={filterMenuStyle}>
            <Text> Projects in {groupCityName}</Text>
            <FlexRow sx={filterRightClusterStyle}>
              {listView === ALL_PROJECTS_TABLE_VIEW_TYPE && (
                <Box sx={keywordInputStyles}>
                  <SearchInput
                    value={keywordFilter}
                    onChange={setKeywordFilter}
                    placeholder="Search projects by keyword"
                  />
                </Box>
              )}
              {companyCount > 0 && (
                <CompanyGroupViewModeToggle mode={listView} onChange={onListViewChange} />
              )}
            </FlexRow>
          </FlexRow>
          {listView === COMPANY_TABLE_VIEW_TYPE ? (
            <CompanyTable
              items={sortedCompanies}
              groupConfigId={Number(group)}
              setCompanyMenu={setCompanyMenu}
              companyMenu={companyMenu}
              setCurrentCompany={setCurrentCompany}
              setCurrentCompanyUuid={setCurrentCompanyUuid}
              setCurrentCompanyName={setCurrentCompanyName}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortColumn={(column) => {
                if (column === sortKey) {
                  setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
                } else {
                  setSortKey(column);
                  setSortDirection(column === 'name' ? 'asc' : 'desc');
                }
              }}
              header={countHeadingText}
            />
          ) : (
            <DashboardLeadsTable
              items={filteredProjectData}
              leadContext={{
                groupId: String(group),
                groupType: ProjectGroupTypes.COMPANY_GROUP,
              }}
              containerSx={{ flex: '0 1 auto' }}
              isLoading={allProjectsLoading}
              header={countHeadingText}
              footer={
                <Pagination
                  currentPage={currentPage}
                  isLoading={allProjectsLoading}
                  setPage={(pageNumber) => setPageNumber(pageNumber)}
                  totalPages={totalProjectPages}
                  onNext={() => nextPage()}
                  onPrev={() => previousPage()}
                />
              }
            />
          )}
        </FlexColumn>
      )}
    </FlexRow>
  );
};

export default CompanyView;
